import React, { useEffect, useMemo, useRef, useState } from "react";

const MedicineDetailModal = ({ medicine, onClose }) => {
  if (!medicine) return null;

  const Section = ({ title, children }) => (
    <div className="medicine-detail-section" style={{ marginBottom: "20px" }}>
      <h5
        style={{
          background: "#e8f0fe",
          padding: "8px 12px",
          borderRadius: "4px",
          color: "#1967d2",
          margin: "0 0 12px 0",
          fontSize: "14px",
          fontWeight: "600",
        }}
      >
        {title}
      </h5>
      <div
        className="medicine-detail-grid"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}
      >
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value }) => (
    <div className="medicine-info-item">
      <div
        style={{ fontSize: "11px", color: "#70757a", textTransform: "uppercase" }}
      >
        {label} :
      </div>
      <div style={{ fontSize: "13px", fontWeight: "500", color: "#202124" }}>
        {value || "—"}
      </div>
    </div>
  );

  return (
    <div
      className="np-modal-backdrop"
      style={{ zIndex: 2000 }}
      onClick={onClose}
    >
      <div
        className="np-modal"
        style={{ maxWidth: "600px", width: "90%", maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="np-modal-header"
          style={{
            borderBottom: "1px solid #dadce0",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4 style={{ margin: 0, fontSize: "18px", color: "#202124" }}>
            Données du médicament
          </h4>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#5f6368",
            }}
          >
            ×
          </button>
        </div>

        <div className="np-modal-body" style={{ padding: "20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "25px",
            }}
          >
            <InfoItem label="NOM DE MARQUE" value={medicine.name} />
            <InfoItem label="DCI" value={medicine.denomination} />
            <InfoItem label="TYPE (GÉNÉRIQUE / PRINCEPS)" value={medicine.type} />
          </div>

          <Section title="2. Forme & présentation">
            <InfoItem label="FORME" value={medicine.forms} />
            <InfoItem label="DOSAGE" value={medicine.dosage} />
            <InfoItem label="CONDITIONNEMENT" value={medicine.conditionnement} />
          </Section>

          <Section title="3. Classification">
            <InfoItem
              label="CLASSE THÉRAPEUTIQUE"
              value={medicine.classe_therapeutique}
            />
            <InfoItem
              label="CLASSE PHARMACOLOGIQUE"
              value={medicine.classe_pharmacological}
            />
            <InfoItem label="LISTE" value={medicine.liste} />
          </Section>

          <Section title="4. Statut & réglementation">
            <InfoItem
              label="LABORATOIRE DÉTENTEUR"
              value={medicine.laboratoires_detenteur}
            />
            <InfoItem label="PAYS DU LABORATOIRE" value={medicine.pays_laboratory} />
            <InfoItem label="N° D'ENREGISTREMENT" value={medicine.registration_no} />
            <InfoItem label="CODE" value={medicine.code} />
            <InfoItem label="STATUT ADMINISTRATIF" value={medicine.statut_admin} />
            <InfoItem
              label="STATUT DE DISPONIBILITÉ"
              value={medicine.statut_disponibilite}
            />
          </Section>

          <Section title="5. Données économiques">
            <InfoItem label="PPA INDICATIF" value={medicine.ppa_indicatif} />
            <InfoItem label="TARIF DE RÉFÉRENCE" value={medicine.tarif_reference} />
            <InfoItem label="REMBOURSEMENT" value={medicine.statut_remboursable} />
          </Section>

          {medicine.molecules_list && medicine.molecules_list.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  marginBottom: "10px",
                  color: "#202124",
                }}
              >
                Substances / Molécules
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {medicine.molecules_list.map((m, i) => (
                  <span
                    key={i}
                    style={{
                      background: "#f1f3f4",
                      padding: "4px 12px",
                      borderRadius: "16px",
                      fontSize: "12px",
                      color: "#3c4043",
                    }}
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className="np-modal-footer"
          style={{
            padding: "16px 20px",
            borderTop: "1px solid #dadce0",
            textAlign: "left",
          }}
        >
          <button
            className="np-btn"
            onClick={onClose}
            style={{ padding: "8px 24px" }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// A searchable medication selector that preserves the current value on blur.
const MedicationSearch = ({
  options = [],
  value,
  onChange,
  onSelectOption,
  onQueryChange,
  placeholder = "Medication name",
  disabled = false,
  clearSignal,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [showDetail, setShowDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [substitutes, setSubstitutes] = useState({ loading: false, data: [], visible: false, for: null });
  const wrapperRef = useRef(null);
  const lastRequestedQueryRef = useRef(null);
  const lastClearSignalRef = useRef(undefined);
  const committedSelectionRef = useRef(null);
  const previousControlledValueRef = useRef(String(value || ""));

  const normalizedOptions = useMemo(() => {
    return options.map((opt) =>
      typeof opt === "string"
        ? { value: opt, label: opt, badge: "MED" }
        : {
            value: opt.value || opt.label || "",
            label: opt.label || opt.value || "",
            badge: opt.badge,
            meta: opt.meta,
            highlight: opt.highlight,
            full: opt.raw || opt, 
          },
    );
  }, [options]);

  const selectedOption = useMemo(
    () =>
      normalizedOptions.find(
        (opt) => opt.value === value || opt.label === value,
      ) || null,
    [normalizedOptions, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Clear results immediately if query is empty OR if we are currently loading new results
    // to avoid showing stale results from a previous search.
    if (!q || isLoading) return [];
    
    return normalizedOptions.filter((opt) => {
      const labelMatch = opt.label.toLowerCase().includes(q);
      const denominationMatch = opt.full?.denomination && opt.full.denomination.toLowerCase().includes(q);
      const moleculeMatch = opt.full?.molecule_fr && opt.full.molecule_fr.toLowerCase().includes(q);
      const brandMatch = opt.full?.name && opt.full.name.toLowerCase().includes(q);
      
      return labelMatch || denominationMatch || moleculeMatch || brandMatch;
    });
  }, [isLoading, normalizedOptions, query]);

  // Close on outside click.
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        const fallback = String(value || "").trim();
        setQuery(
          committedSelectionRef.current?.label ||
            selectedOption?.label ||
            fallback ||
            "",
        );
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [selectedOption, value]);

  // Keep query synced with controlled value when external change occurs.
  useEffect(() => {
    // If a clearSignal was recently applied and matches the last clear token,
    // do not overwrite the visible query with the controlled value.
    if (typeof lastClearSignalRef.current !== "undefined" && lastClearSignalRef.current === clearSignal) {
      return;
    }
    const controlledValue = String(value || "");
    if (!controlledValue && previousControlledValueRef.current) {
      committedSelectionRef.current = null;
    } else if (controlledValue) {
      committedSelectionRef.current = {
        value: controlledValue,
        label: controlledValue,
      };
    }
    previousControlledValueRef.current = controlledValue;
    setQuery(controlledValue);
  }, [value]);

  // External signal to clear the visible query (does not mutate parent value).
  useEffect(() => {
    if (typeof clearSignal === "undefined") return;
    // remember this clear token so the value-sync effect can skip the immediate
    // controlled value assignment that would otherwise refill the input.
    lastClearSignalRef.current = clearSignal;
    committedSelectionRef.current = null;
    setQuery("");
    setOpen(false);
    setSubstitutes({ loading: false, data: [], visible: false, for: null });
  }, [clearSignal]);

  // Notify parent when query changes so results can be fetched from API.
  useEffect(() => {
    if (!onQueryChange || disabled) return;
    
    // We still want to avoid redundant calls, but let's be less restrictive
    // to ensure the search triggers when the user expects it.
    if (lastRequestedQueryRef.current === query && query !== "") return;
    
    const timer = setTimeout(async () => {
      lastRequestedQueryRef.current = query;
      try {
        await onQueryChange(query || "");
      } finally {
        setIsLoading(false);
      }
    }, 150); // Debounce reduced to 150ms for more responsiveness
    
    return () => clearTimeout(timer);
  }, [disabled, onQueryChange, query]);

  const commitSelection = (option) => {
    if (!option) return;
    committedSelectionRef.current = option;
    if (onSelectOption) {
      onSelectOption(option);
    } else {
      onChange?.(option.label || option.value);
    }
    setQuery(option.label);
    // Sync the last requested query with the selected label to prevent 
    // blocking a subsequent search if the user deletes and types the same term.
    lastRequestedQueryRef.current = option.label;
    setOpen(false);
    setSubstitutes({ loading: false, data: [], visible: false, for: null });
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    committedSelectionRef.current = null;
    setQuery(val);
    setOpen(true);
    // Set loading immediately to clear stale results and show spinner
    setIsLoading(true);
    lastRequestedQueryRef.current = null;
    
    // If the input is cleared, notify the parent immediately to clear the options list
    if (!val.trim()) {
      onQueryChange?.("");
    }
  };

  const handleBlurValidate = () => {
    if (committedSelectionRef.current) {
      setQuery(committedSelectionRef.current.label);
      return;
    }
    if (selectedOption) {
      setQuery(selectedOption.label);
      return;
    }
    const exact = normalizedOptions.find(
      (opt) => opt.label.toLowerCase() === query.trim().toLowerCase(),
    );
    if (exact) {
      commitSelection(exact);
    } else {
      // Keep the existing block medication instead of clearing it on incidental blur.
      const fallback = query.trim() || String(value || "").trim();
      if (!fallback) {
        setQuery("");
        onChange?.("");
        return;
      }
      setQuery(fallback);
      onChange?.(fallback);
    }
  };

  const handleShowSubstitutes = async (e, opt) => {
    e.stopPropagation();
    if (substitutes.for === opt.value && substitutes.visible) {
      setSubstitutes((prev) => ({ ...prev, visible: false }));
      return;
    }

    const denomination = opt.full?.denomination;
    if (!denomination) return;

    setSubstitutes({ loading: true, data: [], visible: true, for: opt.value });

    try {
      const { searchMedicinesDetailsApi } = await import("../../../services/apiService");
      const fd = new FormData();
      fd.append("denomination", denomination);
      const res = await searchMedicinesDetailsApi(fd);
      if (res.data?.res) {
        setSubstitutes({
          loading: false,
          data: res.data.list || [],
          visible: true,
          for: opt.value,
        });
      }
    } catch (err) {
      setSubstitutes((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="np-med-search" ref={wrapperRef}>
      <div
        className="np-med-search-input"
        onClick={() => !disabled && setOpen(true)}
      >
        <input
          value={query}
          onChange={handleInputChange}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={handleBlurValidate}
          placeholder={placeholder}
          disabled={disabled}
          style={{ paddingRight: '30px' }}
        />
        <span className="np-med-search-icon">
          {isLoading ? (
            <div className="np-spinner-mini" style={{ width: '14px', height: '14px', border: '2px solid #ccc', borderTopColor: '#637eff', borderRadius: '50%', animation: 'np-spin 0.6s linear infinite' }}></div>
          ) : (
            "🔍"
          )}
        </span>
      </div>
      <style>{`
        @keyframes np-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {open && !disabled ? (
        <div className="np-med-search-menu">
          {filtered.length === 0 ? (
            <div className="np-med-search-empty">No match</div>
          ) : (
            filtered.map((opt, index) => (
              <div key={`${opt.value}-${index}`} className="np-med-search-option-container">
                <button
                  type="button"
                  className={
                    opt.value === value
                      ? "np-med-search-option active"
                      : "np-med-search-option"
                  }
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commitSelection(opt)}
                >
                  <div className="np-med-search-main">
                    <div className="np-med-search-label">{opt.label}</div>
                    {opt.badge ? (
                      <span className="np-med-search-badge">{opt.badge}</span>
                    ) : null}
                  </div>

                  {opt.full && (
                    <div
                      className="np-med-search-details"
                      style={{
                        fontSize: "10px",
                        color: "#666",
                        marginTop: "4px",
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {opt.full.remboursable && (
                        <span>Remb: {opt.full.remboursable}</span>
                      )}
                      {opt.full.type_med && (
                        <span>Type: {opt.full.type_med}</span>
                      )}
                      {opt.full.ppa && <span>PPA: {opt.full.ppa}</span>}
                      {opt.full.tarif && <span>Tarif: {opt.full.tarif}</span>}
                    </div>
                  )}

                  <div className="np-med-search-actions" style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                    <span 
                      className="np-btn-soft" 
                      style={{ padding: '2px 6px', fontSize: '10px' }}
                      onClick={(e) => { e.stopPropagation(); setShowDetail(opt.full); }}
                    >
                      + Info
                    </span>
                    {opt.full?.denomination && (
                      <span 
                        className="np-btn-soft" 
                        style={{ padding: '2px 6px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '3px' }}
                        onClick={(e) => handleShowSubstitutes(e, opt)}
                      >
                        Equivalent {substitutes.visible && substitutes.for === opt.value ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </button>

                {substitutes.visible && substitutes.for === opt.value && (
                  <div className="np-med-substitutes" style={{ padding: '8px 10px', background: '#fcfdff', borderLeft: '3px solid #637eff', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '8px', color: '#637eff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Équivalents disponibles :
                    </div>
                    {substitutes.loading ? (
                      <div style={{ fontSize: '11px', color: '#666', padding: '5px 0' }}>Chargement...</div>
                    ) : substitutes.data.length === 0 ? (
                      <div style={{ fontSize: '11px', color: '#999', padding: '5px 0' }}>Aucun équivalent trouvé</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {substitutes.data.filter(s => s.id !== opt.full?.id).map(sub => (
                          <div 
                            key={sub.id} 
                            className="np-med-sub-item"
                            style={{ 
                              padding: '6px 8px', 
                              borderRadius: '4px', 
                              cursor: 'pointer',
                              border: '1px solid #edf2f7',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = '#f0f4ff';
                              e.currentTarget.style.borderColor = '#637eff';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.borderColor = '#edf2f7';
                            }}
                            onClick={() => commitSelection({ value: sub.name + (sub.dosage ? ' ' + sub.dosage : ''), label: sub.display_label || sub.name })}
                          >
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#2d3748' }}>
                              {sub.display_label || sub.name}
                            </div>
                            <div style={{ 
                              fontSize: '10px', 
                              color: '#718096', 
                              marginTop: '3px',
                              display: 'flex',
                              gap: '10px'
                            }}>
                              {sub.type_med && <span>Type: {sub.type_med}</span>}
                              {sub.ppa && <span>PPA: {sub.ppa} DA</span>}
                              {sub.tarif && <span>Tarif: {sub.tarif} DA</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : null}

      {showDetail && (
        <MedicineDetailModal
          medicine={showDetail}
          onClose={() => setShowDetail(null)}
        />
      )}
    </div>
  );
};

export default MedicationSearch;
